/**
 * Helpers defensivos para llamadas Mapbox GL que pueden tirar si `map.style`
 * aún no se inicializó o ya fue destruido (race con `map.remove()`).
 *
 * Bug observado por Diego 2026-05-11:
 *   TypeError: undefined is not an object (evaluating 'this.style.getOwnLayer')
 *     at getLayer (vendor-mapbox)
 * El stack apunta a `map.getLayer(id)` ejecutándose entre `mount` y
 * `style.load`, o tras `unmount` en React Strict Mode (doble-mount). Mapbox
 * GL NO devuelve `undefined` en ese estado — lanza un TypeError sincrónico
 * que escala como uncaught en el handler de click/move.
 *
 * Estos wrappers absorben el throw y devuelven un fallback seguro.
 */
import type * as mapboxgl from 'mapbox-gl';

/**
 * Versión defensiva de `map.getLayer(id)`. Devuelve `true` si el layer
 * existe y es accesible, `false` en caso contrario (incluido cuando
 * `map.style` es undefined o el map fue removido).
 *
 * Usar en lugar de `if (map.getLayer(id))` para filtros/early-returns.
 */
export function hasLayer(map: mapboxgl.Map, id: string): boolean {
  try {
    return Boolean(map.getLayer(id));
  } catch {
    return false;
  }
}

/**
 * Igual que `hasLayer` pero retorna el objeto layer o `undefined` — usar
 * cuando además necesites inspeccionar el layer (e.g., leer paint props).
 */
export function safeGetLayer(map: mapboxgl.Map, id: string): ReturnType<mapboxgl.Map['getLayer']> | undefined {
  try {
    return map.getLayer(id);
  } catch {
    return undefined;
  }
}
